import { useCallback, useMemo, useState } from "react";

import { Alert, Box, List, ListItem, Typography } from "@mui/material";

import { allocationTxt } from "../../../../assets/texts/CloudAnalytics/allocation";
import useAllocationDndStore from "./useAllocationDnd";

type UseAllocationDialogProps = {
  isSaveAs: boolean | undefined;
  isDuplicate: boolean;
  getWarnings: () => string[];
  handleSave: (name?: string) => Promise<boolean>;
  items: { main: string[] };
  hasUnallocated: boolean;
  isEdit?: boolean;
};

const WarningList = ({ warnings }: { warnings: string[] }) => (
  <List dense sx={{ listStyleType: "disc" }}>
    {warnings.map((warning, index) => (
      <ListItem key={index} dense sx={{ listStyleType: "disc", display: "list-item", p: 0 }}>
        <Typography variant="body2">{warning}</Typography>
      </ListItem>
    ))}
  </List>
);

export const useAllocationSaveDialog = ({
  isSaveAs,
  isDuplicate,
  getWarnings,
  handleSave,
  items,
  hasUnallocated,
  isEdit,
}: UseAllocationDialogProps) => {
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const { allocationName } = useAllocationDndStore();
  const canOpenSaveDialog = items.main.length > 1 || hasUnallocated || isDuplicate || isEdit;

  const handleSaveDialog = useCallback(() => {
    const currentWarnings = getWarnings();
    setWarnings(currentWarnings);
    if (allocationName && !isEdit) {
      handleSave(allocationName);
      return;
    }

    if (canOpenSaveDialog || currentWarnings.length) {
      setOpenSaveDialog(true);
    } else {
      handleSave();
    }
  }, [getWarnings, allocationName, isEdit, canOpenSaveDialog, handleSave]);

  const handleSaveAsDialog = useCallback(() => {
    setOpenSaveDialog(true);
  }, []);

  const handleDialogSave = useCallback(
    async (name?: string) => {
      const success = await handleSave(name);
      if (success) {
        setOpenSaveDialog(false);
      }
    },
    [handleSave]
  );

  const handleDialogClose = useCallback(() => {
    setOpenSaveDialog(false);
  }, []);

  const saveLabel = useMemo(() => {
    if (isSaveAs) return allocationTxt.ALLOCATION_SAVE_AS;
    if (isDuplicate) return allocationTxt.ALLOCATION_DUPLICATE;
    return allocationTxt.ALLOCATION_SAVE;
  }, [isDuplicate, isSaveAs]);

  const modalText = useMemo(() => {
    if (isDuplicate) {
      return {
        title: allocationTxt.ALLOCATION_DUPLICATE_SAVE_TITLE,
        description: allocationTxt.ALLOCATION_DUPLICATE_SAVE_DESCRIPTION,
        confirmButtonText: allocationTxt.ALLOCATION_SAVE_CONFIRM,
      };
    }
    if (isSaveAs) {
      return {
        title: allocationTxt.ALLOCATION_SAVE_AS_TITLE,
        description: allocationTxt.ALLOCATION_SAVE_DESCRIPTION,
        confirmButtonText: allocationTxt.ALLOCATION_SAVE_CONFIRM,
        helperContent: (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {allocationTxt.ALLOCATION_EDIT_SAVE_WARNING}
            {!!warnings.length && <WarningList warnings={warnings} />}
          </Alert>
        ),
      };
    }
    if (items.main.length === 1 && !hasUnallocated && isEdit) {
      return {
        title: allocationTxt.SINGLE_ALLOCATION_EDIT_SAVE_AS_TITLE,
        description: warnings.length
          ? allocationTxt.SINGLE_ALLOCATION_EDIT_SAVE_DESCRIPTION_WARNINGS
          : allocationTxt.SINGLE_ALLOCATION_EDIT_SAVE_DESCRIPTION,
        helperContent: <Box sx={{ mt: 1, ml: 2 }}>{!!warnings.length && <WarningList warnings={warnings} />}</Box>,
        hideInput: true,
        confirmButtonText: allocationTxt.ALLOCATION_SAVE_CONFIRM,
      };
    }
    if ((items.main.length > 1 || hasUnallocated) && isEdit) {
      return {
        title: allocationTxt.ALLOCATION_SAVE_TITLE,
        description: allocationTxt.ALLOCATION_SAVE_EDIT_DESCRIPTION,
        helperContent: (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {warnings.length
              ? allocationTxt.ALLOCATION_EDIT_SAVE_WARNING
              : allocationTxt.SINGLE_ALLOCATION_EDIT_SAVE_DESCRIPTION}
            {!!warnings.length && <WarningList warnings={warnings} />}
          </Alert>
        ),
      };
    }

    return {
      title: allocationTxt.ALLOCATION_SAVE_TITLE,
      description: allocationTxt.ALLOCATION_SAVE_DESCRIPTION,
    };
  }, [hasUnallocated, isDuplicate, isEdit, isSaveAs, items.main.length, warnings]);

  return {
    openSaveDialog,
    warnings,
    handleSaveDialog,
    handleSaveAsDialog,
    handleDialogSave,
    handleDialogClose,
    saveLabel,
    modalText,
  };
};
